import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare } from "lucide-react";
import ConversationsList from "./ConversationsList";
import ConversationViewDesktop from "./ConversationViewDesktop";

export default function DesktopConversationsLayout({
  activeDoctor,
}: {
  activeDoctor?: any;
}) {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [listWidth, setListWidth] = useState(360);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isResizingRef = useRef(false);

  const handleListClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Handle link clicks (patient conversations)
      const link = target.closest(
        'a[href^="/conversations/"]'
      ) as HTMLAnchorElement | null;
      if (link) {
        event.preventDefault();
        const href = link.getAttribute("href") || "";
        const encodedUser = href.split("/conversations/")[1];
        if (!encodedUser) return;
        setSelectedUser(decodeURIComponent(encodedUser));
        return;
      }

      // Handle button clicks (digest pseudo-thread row uses navigate, but
      // intercept data-digest-id clicks if needed in future)
    },
    []
  );

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isResizingRef.current) return;
    const containerLeft =
      containerRef.current?.getBoundingClientRect().left ?? 0;
    const nextWidth = Math.min(
      520,
      Math.max(280, event.clientX - containerLeft)
    );
    setListWidth(nextWidth);
  }, []);

  const stopResize = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResize);
  }, [handleMouseMove]);

  const startResize = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      isResizingRef.current = true;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResize);
    },
    [handleMouseMove, stopResize]
  );

  useEffect(() => {
    return () => stopResize();
  }, [stopResize]);

  useEffect(() => {
    const handler = () => setSelectedUser(null);
    window.addEventListener("digest-hidden", handler);
    return () => window.removeEventListener("digest-hidden", handler);
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 w-full border rounded-lg shadow-sm overflow-hidden bg-white"
    >
      <div
        className="shrink-0 h-full min-h-0 overflow-y-auto"
        style={{ width: listWidth }}
        onClickCapture={handleListClickCapture}
      >
        <ConversationsList
          activeDoctor={activeDoctor}
          selectedUser={selectedUser}
          onSelectItem={setSelectedUser}
        />
      </div>
      <div
        className="relative w-px shrink-0 cursor-col-resize bg-slate-200 hover:bg-primary/40 transition-colors after:content-[''] after:absolute after:inset-y-0 after:left-1/2 after:w-4 after:-translate-x-1/2"
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize conversation list"
      />
      <div className="flex flex-col flex-1 h-full min-h-0 overflow-hidden">
        {selectedUser ? (
          <ConversationViewDesktop
            utilisateur={selectedUser}
            onOpenConversation={(utilisateur) => setSelectedUser(utilisateur)}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-slate-400 select-none">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center shadow-sm ring-1 ring-slate-200/60">
              <MessageSquare className="w-8 h-8 text-primary/50" strokeWidth={1.5} />
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-slate-500">No conversation selected</p>
              <p className="text-xs text-slate-400">Choose a chat from the list to get started</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
